import { CAREER_GUIDANCE_NOTICE } from '@worksim/engine';
import { catalog, interview, learn } from '../../api/client';
import { useAsync } from '../../hooks/useAsync';
import { href } from '../../router';
import { Icon, MarqueeBanner, Skeleton, useCountUp, type IconName } from '../../components/ui';
import { familyIcon } from '../../lib/careerMap';
import { ProductPreview } from './ProductPreview';
import '../../styles/pages/discover.css';

/**
 * The signed-out landing page. Every number on it comes from the live catalog: nothing about the
 * size of the platform is written into the markup. The product preview uses the real interface
 * components with a fixed sample, and says so.
 */

const HOW_IT_WORKS: { icon: IconName; title: string; body: string }[] = [
  { icon: 'book', title: 'Learn', body: 'Short lessons and certification-aligned material explain the concepts behind the work, in the order you meet them.' },
  { icon: 'terminal', title: 'Practice', body: 'Hands-on labs in simulated tools: a terminal, a ticket queue, an identity console, a query editor. Nothing you do can break anything.' },
  { icon: 'briefcase', title: 'Apply', body: 'Full workplace scenarios at TechCore Solutions. Gather evidence, talk to colleagues, decide, and produce a real deliverable.' },
  { icon: 'target', title: 'Interview', body: 'Prepare with question banks and mock rounds built on the work you have already done, so your examples are your own.' },
];

const CAREER_MARQUEE = ['HELP DESK', 'SYSTEMS ANALYST', 'BUSINESS ANALYST', 'DATA ANALYST', 'CLOUD SUPPORT', 'CUSTOMER SUCCESS', 'IT SECURITY', 'NETWORK SUPPORT'];
const VERB_MARQUEE = ['INVESTIGATE', 'ANALYZE', 'COMMUNICATE', 'DOCUMENT', 'SOLVE', 'COLLABORATE', 'IMPROVE'];

export function LandingPage() {
  const facts = useAsync(async () => {
    const [careers, scenarios, labs, kits] = await Promise.all([catalog.careers(), catalog.scenarios(), learn.labs(), interview.kits()]);
    return { careers, scenarios: scenarios.scenarios, labs: labs.labs, kits: kits.kits };
  }, []);

  const careers = facts.data?.careers.careers ?? [];
  const families = Object.entries(facts.data?.careers.families ?? {});
  const availableCount = careers.filter((c) => c.status === 'available').length;
  const teasers = (facts.data?.scenarios ?? []).filter((s) => s.kind === 'introductory').slice(0, 3);

  return (
    <div className="landing page-enter">
      {/* ---- hero ---- */}
      <section className="landing-band navy" aria-labelledby="hero-title">
        <div className="inner hero">
          <div>
            <span className="eyebrow">Career simulation at TechCore Solutions</span>
            <h1 id="hero-title">Experience the job before you get hired.</h1>
            <p className="lede">Build practical workplace skills through interactive simulations, AI-powered mentorship, and realistic career experiences.</p>
            <div className="actions">
              <a className="button large on-navy" href={href({ page: 'register' })}>Start your career journey</a>
              <a className="button large on-navy secondary" href={href({ page: 'explore' })}>Explore career tracks</a>
            </div>
            <div className="hero-proof">
              <HeroStat loading={facts.loading} value={careers.length} label="career tracks to explore" />
              <HeroStat loading={facts.loading} value={availableCount} label="fully simulated today" />
              <HeroStat loading={facts.loading} value={facts.data?.scenarios.length ?? 0} label="workplace scenarios" />
            </div>
          </div>
          <ProductPreview />
        </div>
      </section>

      {/* ---- marquees ---- */}
      <section className="landing-band navy tight" aria-label="What you can practice" style={{ paddingTop: 0 }}>
        <div className="inner">
          <MarqueeBanner items={CAREER_MARQUEE} onNavy speed="slow" label="Career categories" />
          <MarqueeBanner items={VERB_MARQUEE} onNavy speed="normal" label="What you do in a scenario" />
        </div>
      </section>

      {/* ---- how it works ---- */}
      <section className="landing-band" aria-labelledby="how-title">
        <div className="inner">
          <div className="section-head">
            <h2 id="how-title">How it works</h2>
            <span className="small muted">Learn → Practice → Apply → Interview</span>
          </div>
          <div className="arrowed">
            {HOW_IT_WORKS.map((s, i) => (
              <article key={s.title} className="card step-card reveal" style={{ ['--i' as string]: i }}>
                <span className="n" aria-hidden="true">{i + 1}</span>
                <span className="icon-tile"><Icon name={s.icon} /></span>
                <h3>{s.title}</h3>
                <p className="small muted">{s.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ---- career categories ---- */}
      <section className="landing-band soft" aria-labelledby="families-title">
        <div className="inner">
          <div className="section-head">
            <h2 id="families-title">Seven families of work</h2>
            <a className="more" href={href({ page: 'explore' })}>Browse every track <Icon name="arrow-right" size={14} /></a>
          </div>
          <p className="small muted" style={{ maxWidth: '62ch', marginTop: 0 }}>Families group tracks by the kind of work they do. They are descriptive: no family, and no track, is ranked above another.</p>
          <div className="tile-grid" style={{ marginTop: 'var(--sp-4)' }}>
            {facts.loading && families.length === 0 && Array.from({ length: 7 }).map((_, i) => (
              <div className="card" key={i} aria-hidden="true"><Skeleton kind="title" /><Skeleton kind="text" /></div>
            ))}
            {families.map(([id, label], i) => {
              const count = careers.filter((c) => c.family === id).length;
              const ready = careers.filter((c) => c.family === id && c.status === 'available').length;
              return (
                <a className="card hoverable family-card reveal" key={id} href={`#/careers?family=${encodeURIComponent(id)}`} style={{ ['--i' as string]: i }}>
                  <span className="icon-tile"><Icon name={familyIcon(id)} /></span>
                  <span>
                    <h3>{label}</h3>
                    <span className="count">{count} track{count === 1 ? '' : 's'} · {ready} simulated now</span>
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---- a real day at TechCore ---- */}
      <section className="landing-band" aria-labelledby="day-title">
        <div className="inner">
          <div className="section-head">
            <h2 id="day-title">A real day at TechCore</h2>
            <span className="small muted">Three scenarios from the catalog</span>
          </div>
          <div className="grid-3">
            {facts.loading && teasers.length === 0 && Array.from({ length: 3 }).map((_, i) => (
              <div className="card" key={i} aria-hidden="true"><Skeleton kind="title" /><Skeleton kind="text" count={3} /></div>
            ))}
            {teasers.map((s, i) => (
              <article className="card teaser reveal" key={s.id} style={{ ['--i' as string]: i }}>
                <div className="meta">
                  <span className="badge cyan">{s.kind}</span>
                  <span className="small muted"><Icon name="clock" size={14} /> about {s.estimatedMinutes} min</span>
                </div>
                <h3 style={{ margin: 0 }}>{s.title}</h3>
                <p className="small muted" style={{ margin: 0 }}>{s.summary}</p>
              </article>
            ))}
            {!facts.loading && teasers.length === 0 && <p className="muted">Scenario teasers are loading from the catalog.</p>}
          </div>
          <div className="actions">
            <a className="button secondary" href={href({ page: 'explore' })}>See which tracks play these scenarios</a>
          </div>
        </div>
      </section>

      {/* ---- platform facts ---- */}
      <section className="landing-band soft" aria-labelledby="facts-title">
        <div className="inner">
          <h2 id="facts-title">What is inside today</h2>
          <p className="small muted" style={{ marginTop: 0 }}>Counted from the live catalog every time this page loads.</p>
          <div className="facts" style={{ marginTop: 'var(--sp-4)' }} aria-live="polite">
            <FactCard loading={facts.loading} icon="compass" value={careers.length} label="career tracks" />
            <FactCard loading={facts.loading} icon="briefcase" value={facts.data?.scenarios.length ?? 0} label="workplace scenarios" />
            <FactCard loading={facts.loading} icon="terminal" value={facts.data?.labs.length ?? 0} label="hands-on labs" />
            <FactCard loading={facts.loading} icon="target" value={facts.data?.kits.length ?? 0} label="interview kits" />
          </div>
          {facts.error && <p className="notice warn" role="status">The catalog counts could not be loaded just now. Everything else on this page still works.</p>}
        </div>
      </section>

      {/* ---- trust ---- */}
      <section className="landing-band" aria-labelledby="trust-title">
        <div className="inner two-col">
          <div>
            <span className="eyebrow">Straight answers</span>
            <h2 id="trust-title" style={{ marginTop: 0 }}>What this is, and what it is not</h2>
            <p className="notice">{CAREER_GUIDANCE_NOTICE}</p>
            <ul className="small muted" style={{ paddingLeft: '1.1rem' }}>
              <li>No track is ranked above another, and nothing here is a job guarantee.</li>
              <li>Certification names appear as study alignment only. WorkSim AI is not affiliated with, or endorsed by, any certification body.</li>
              <li>Evaluations explain what each check looked for and what it found, so you can argue with them.</li>
              <li>Your work, sessions and evaluations are yours: export or delete them from your profile at any time.</li>
            </ul>
          </div>
          <aside className="card accent">
            <h3 style={{ marginTop: 0 }}>Built around one company</h3>
            <p className="small muted">TechCore Solutions has departments, managers, policies, a ticket queue, an inbox, a knowledge base and dashboards. Every track works inside the same company, so a hand-off to another team is a hand-off to somebody real in the simulation.</p>
            <a className="button secondary w-full" href={href({ page: 'about' })}>How the simulator works</a>
          </aside>
        </div>
      </section>

      {/* ---- final CTA ---- */}
      <section className="landing-band navy" aria-labelledby="cta-title">
        <div className="inner cta-final">
          <span className="eyebrow">Start where you are</span>
          <h2 id="cta-title">Pick a track, take the first ticket.</h2>
          <p className="lede" style={{ margin: '0 auto', maxWidth: '54ch' }}>Create an account to run simulations and keep your progress, or explore and compare every track without one.</p>
          <div className="actions">
            <a className="button large on-navy" href={href({ page: 'register' })}>Start your career journey</a>
            <a className="button large on-navy secondary" href={href({ page: 'career-map' })}>See the career map</a>
          </div>
        </div>
      </section>
    </div>
  );
}

function HeroStat({ value, label, loading }: { value: number; label: string; loading: boolean }) {
  const shown = useCountUp(value);
  return (
    <div className="stat">
      {loading ? <Skeleton kind="title" width="3.5rem" /> : <span className="value tabular">{shown}</span>}
      <span className="label">{label}</span>
    </div>
  );
}

function FactCard({ value, label, icon, loading }: { value: number; label: string; icon: IconName; loading: boolean }) {
  const shown = useCountUp(value);
  return (
    <div className="card">
      <span className="icon-tile" aria-hidden="true"><Icon name={icon} /></span>
      <div className="stat" style={{ marginTop: 'var(--sp-2)' }}>
        {loading ? <Skeleton kind="title" width="4rem" /> : <span className="value tabular">{shown}</span>}
        <span className="label">{label}</span>
      </div>
    </div>
  );
}
